import {
  Box,
  Chip,
  ListItem,
  TextField,
  Autocomplete,
  ListItemText,
  ListItemAvatar,
  CircularProgress
} from "@material-ui/core";
import { useMemo, useState } from "react";
import useAuth from "hooks/useAuth";
import useWebotServer from "hooks/useWebotServer";
import useContactSearch from "hooks/contacts/useContactSearch";
import useContactsLookup, { useContactLookup } from "hooks/contacts/useContactsLookup";
import { readContactDocument } from "utils/contacts/readContactDocument";
import { buildContactPickerOptions } from "./contactPickerOptions";
import type { ContactPickerOption } from "./contactPickerOptions";
import { useContactHandlers } from "hooks/crm/useContactHandler";
import CrmHandlerBadge from "components/_dashboard/crm/handler/CrmHandlerBadge";
import CrmMemberAvatar from "components/_dashboard/crm/org/CrmMemberAvatar";
import type { CrmHandlerView } from "components/_dashboard/crm/handler/crmHandlerFace";
import { getFullName } from "utils/Haim/utils";
import { useTranslation } from "react-i18next";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { ContactTableAvatar } from "../contacts/table/rows/ContactTableAvatar";
import type { TFunction } from "react-i18next";
import type { SyntheticEvent } from "react";
import type { RealEstateContact } from "../../../@types/sharedSchema";
import type {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState
} from "@material-ui/core";

/**
 * THE CONTACT PICKER — choose one (or several) contacts, or type a new number.
 *
 * ── Where the options come from ─────────────────────────────────────────────────────
 *
 * From the server, as the realtor types (`useContactSearch`): the few dozen contacts that
 * match, by name or by phone in any spelling, and the most recently touched ones before
 * anything is typed.
 *
 * They used to be `useLegacyContactsBook().getAllContacts()` — the whole contact book, downloaded into
 * the browser so that this menu could filter it. Seven forms mount this picker (a calendar
 * meeting, a signature, a listing seller, a search), so opening any of them paid for the
 * whole book: seconds, megabytes, up to 92,069 contacts, to choose one.
 *
 * ── What it hands back ──────────────────────────────────────────────────────────────
 *
 * A WHOLE CONTACT, exactly as before — that contract is what the forms are written
 * against (the signature form prefills an email and an ID number from it, the listings
 * search seeds a budget range).
 *
 * An option in the menu is only a looked-up SUMMARY: enough to draw a name, a phone and a
 * hot flag. So the moment an option is picked its document is read
 * (`readContactDocument`) and THAT is what the host receives. One document read per pick,
 * instead of the whole book per open. If the read fails, the host gets the id and the name
 * the option showed — the same thing it gets for a typed number that is not a contact.
 *
 * A number typed in that is not a contact comes back as `{ id }` (the E.164 phone), as it
 * always has. "Add this number" is offered only once the lookup has ANSWERED that the
 * number is not a contact; while it has not, the menu waits rather than offer to add
 * somebody who may already be there.
 */

type NewContactPickerProps = {
  label?: string;
  loading?: boolean;
  /**
   * Options the HOST supplies, listed above the search results: people who belong to what
   * the form is about and may not be contacts at all (the attorney of a listing, its
   * seller). They are handed back as they are, without a document read.
   */
  pinnedOptions?: ContactPickerOption[];
  selectedContact: RealEstateContact | null;
  disabledOptions?: string[];
  setSelectedContact: (contact: RealEstateContact | null) => void;
};

/**
 * How many of the visible options the handler badge asks about. The assignments endpoint
 * takes a page of ids; the menu shows a few dozen rows, and the SELECTED contacts are always
 * included — the badge on a chosen chip is the one that has to survive the menu closing.
 */
const HANDLER_LOOKUP_CAP = 25;

const labelOf = (contact: ContactPickerOption | null | undefined): string =>
  contact ? getFullName(contact.callerFirstName, contact.callerLastName) || contact.id || "" : "";

/**
 * The options, the search behind them, and the pick — shared by the single and the
 * multiple picker, which differ only in what they do with a pick.
 */
function useContactPickerOptions(
  pinnedOptions: ContactPickerOption[] | undefined,
  selected: ContactPickerOption[]
) {
  const { user } = useAuth();
  const userEmail = user?.email as string | undefined;
  // The pick's document comes from where this browser reads contacts (`readContactDocument`).
  const { contactsGet } = useWebotServer();

  const [inputValue, setInputValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [readingPick, setReadingPick] = useState(false);

  // Names for selections that arrive as bare `{ id }` stand-ins (a host that only knows the
  // phones).
  const selectedIds = useMemo(
    () => selected.map((contact) => contact.id).filter(Boolean) as string[],
    [selected]
  );
  const { lookup } = useContactsLookup(selectedIds);

  /** A selection as it should be DRAWN: its own names, else the looked-up ones. */
  const named = (contact: ContactPickerOption): ContactPickerOption => {
    if (contact.callerFirstName || contact.callerLastName) return contact;
    const known = contact.id ? lookup(contact.id) : null;
    return known
      ? { ...contact, callerFirstName: known.callerFirstName, callerLastName: known.callerLastName }
      : contact;
  };

  // After a pick the input holds the label of the chosen contact. That is not a search: on
  // reopening, the menu shows the recent contacts, as it did before anything was typed.
  const typed = inputValue.trim();
  const isLabelOfSelection = selected.length === 1 && typed === labelOf(named(selected[0]));
  const term = isLabelOfSelection ? "" : typed;

  const search = useContactSearch(term, { enabled: menuOpen });

  // A phone number is also asked about directly: the search matches substrings and returns
  // a page, while "is THIS number a contact" needs one definite answer. Asked only once the
  // search for the same text has come back — the phone parser accepts a number well before
  // the realtor has finished typing it, and each partial would otherwise be its own request.
  const typedPhone = useMemo(
    () => (term ? (parsePhoneNumber(term)?.format("E.164") as string | undefined) ?? null : null),
    [term]
  );
  const typedContact = useContactLookup(typedPhone, {
    enabled: menuOpen && Boolean(typedPhone) && !search.loading
  });

  const pinnedIds = useMemo(
    () => new Set((pinnedOptions ?? []).map((option) => option.id)),
    [pinnedOptions]
  );

  // What the menu lists, and when it offers "add this number" — the rule, with its reasons
  // and its tests, lives in `contactPickerOptions.ts`.
  const options = useMemo(
    () =>
      buildContactPickerOptions({
        pinnedOptions,
        searchResults: search.results,
        searching: search.loading,
        term,
        typedPhone,
        typedContact
      }),
    [pinnedOptions, search.results, search.loading, term, typedPhone, typedContact]
  );

  // The handler badge, for what is on screen.
  const askAbout = useMemo(() => {
    const shown = options.filter((option) => !option.hint).slice(0, HANDLER_LOOKUP_CAP);
    return [...new Set([...selectedIds, ...shown.map((option) => option.id)])];
  }, [selectedIds, options]);
  const { handlerFor } = useContactHandlers(askAbout);

  /**
   * Turns what the Autocomplete emitted into what the host receives.
   *
   * `null` means "nothing to add": typed text that is not a phone number, or a number that
   * is already among `alreadySelected`.
   */
  const resolvePick = async (
    entry: string | ContactPickerOption,
    alreadySelected: ContactPickerOption[] = []
  ): Promise<RealEstateContact | null> => {
    let stub: RealEstateContact;

    if (typeof entry === "string" || entry.hint) {
      // Typed in: a string from Enter on free text, or the "add this number" row.
      const validPhoneNumber = parsePhoneNumber(typeof entry === "string" ? entry : entry.id);
      if (!validPhoneNumber) return null;

      const phone = validPhoneNumber.format("E.164") as string;
      if (alreadySelected.some(({ id }) => id === phone)) return null;

      // The id IS the phone — one field, no mirror to drift from it.
      stub = { id: phone } as RealEstateContact;
    } else {
      // An option the host supplied is the object the host made; there is no document for it.
      if (pinnedIds.has(entry.id)) return entry as RealEstateContact;

      stub = {
        id: entry.id,
        callerFirstName: entry.callerFirstName,
        callerLastName: entry.callerLastName
      } as RealEstateContact;
    }

    if (!userEmail) return stub;

    setReadingPick(true);
    try {
      // The whole contact, which is what every host of this picker is written against. A
      // typed number is read too: it may be a contact the realtor simply typed out in full.
      return (await readContactDocument(contactsGet, userEmail, stub.id)) ?? stub;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("NewContactPicker: could not read the picked contact; handing over its id and name", error);
      return stub;
    } finally {
      setReadingPick(false);
    }
  };

  return {
    options,
    named,
    handlerFor,
    resolvePick,
    busy: search.loading || readingPick,
    autocompleteProps: {
      onInputChange: (_event: SyntheticEvent, value: string) => setInputValue(value),
      onOpen: () => setMenuOpen(true),
      onClose: () => setMenuOpen(false),
      // The options ARE the answer to what was typed. Filtering them again here would hide
      // a contact the server matched on something this menu does not print (a tag).
      filterOptions: (shown: ContactPickerOption[]) => shown
    }
  };
}

export function NewContactPicker({
  label,
  loading,
  pinnedOptions,
  disabledOptions,
  selectedContact,
  setSelectedContact
}: NewContactPickerProps): JSX.Element {
  const { t: translate } = useTranslation();

  const selected = useMemo(() => (selectedContact ? [selectedContact] : []), [selectedContact]);
  const { options, named, handlerFor, resolvePick, busy, autocompleteProps } =
    useContactPickerOptions(pinnedOptions, selected);

  const handleSelectedContactChange = async (
    _event: SyntheticEvent,
    newContact: null | string | ContactPickerOption
  ): Promise<void> => {
    setSelectedContact(newContact === null ? null : await resolvePick(newContact));
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      blurOnSelect
      value={selectedContact ?? null}
      options={options}
      disabled={Boolean(loading)}
      onChange={handleSelectedContactChange}
      renderInput={handleRenderInput(translate, Boolean(loading) || busy, label)}
      renderOption={handleRenderOption(translate, handlerFor)}
      getOptionLabel={handleGetOptionLabel(named)}
      getOptionDisabled={handleGetOptionDisabled(disabledOptions)}
      isOptionEqualToValue={handleIsOptionEqualToValue}
      {...autocompleteProps}
    />
  );
}

type NewMultipleContactPickerProps = {
  label?: string;
  loading?: boolean;
  /** See `NewContactPickerProps.pinnedOptions`. */
  pinnedOptions?: ContactPickerOption[];
  disabledOptions?: string[];
  selectedContacts: RealEstateContact[];
  onChangeAction?: (selectedContacts?: RealEstateContact[]) => void;
  setSelectedContacts: (contact: RealEstateContact[]) => void;
};

export function NewMultipleContactPicker({
  label,
  loading,
  pinnedOptions,
  disabledOptions,
  selectedContacts,
  onChangeAction,
  setSelectedContacts
}: NewMultipleContactPickerProps): JSX.Element {
  const { t: translate } = useTranslation();

  const { options, named, handlerFor, resolvePick, busy, autocompleteProps } =
    useContactPickerOptions(pinnedOptions, selectedContacts);

  const handleSelectedContactsChange = async (
    _event: SyntheticEvent,
    newContacts: (string | ContactPickerOption)[]
  ): Promise<void> => {
    const resolved = await Promise.all(
      newContacts.map((entry) => {
        // A selection that was already there keeps the object the host holds for it.
        const kept =
          typeof entry === "string" ? undefined : selectedContacts.find(({ id }) => id === entry.id);
        return kept ?? resolvePick(entry, selectedContacts);
      })
    );
    const newParsedContacts = resolved.filter(Boolean) as RealEstateContact[];

    setSelectedContacts(newParsedContacts);

    onChangeAction?.(newParsedContacts);
  };

  return (
    <Autocomplete
      freeSolo
      multiple
      fullWidth
      blurOnSelect
      value={selectedContacts}
      options={options}
      disabled={Boolean(loading)}
      onChange={handleSelectedContactsChange}
      renderTags={handleRenderTag(named, handlerFor)}
      renderInput={handleRenderInput(translate, Boolean(loading) || busy, label)}
      renderOption={handleRenderOption(translate, handlerFor)}
      getOptionLabel={handleGetOptionLabel(named)}
      getOptionDisabled={handleGetOptionDisabled(disabledOptions)}
      isOptionEqualToValue={handleIsOptionEqualToValue}
      {...autocompleteProps}
    />
  );
}

function handleGetOptionDisabled(disabledOptions: string[] | undefined) {
  return (option: ContactPickerOption): boolean => {
    if (!disabledOptions?.length) return false;

    const isOptionOnDisabledOptions = disabledOptions.includes(option.id);

    return isOptionOnDisabledOptions;
  };
}

type HandlerLookup = (contactId: string) => CrmHandlerView | null | undefined;

function handleRenderOption(translate: TFunction, handlerFor?: HandlerLookup) {
  return (
    props: React.HTMLAttributes<HTMLLIElement>,
    {
      hint,
      crmIsHot,
      callerLastName,
      callerFirstName,
      id: contactPhone
    }: ContactPickerOption,
    _state: AutocompleteRenderOptionState
  ): JSX.Element => {
    const callerFullName = getFullName(callerFirstName, callerLastName);

    const validPhoneNumber = parsePhoneNumber(contactPhone);

    const formattedLocalPhoneNumber = validPhoneNumber?.formatNational() ?? contactPhone;

    const isNewOptionHasInvalidPhoneNumber = hint && !validPhoneNumber;

    return (
      <ListItem {...props} disabled={isNewOptionHasInvalidPhoneNumber} key={contactPhone}>
        <ListItemAvatar>
          <ContactTableAvatar
            badgeSize={16}
            avatarSize={32}
            callerFullName={callerFullName}
            crmIsHot={crmIsHot === true}
          />
        </ListItemAvatar>
        {hint ? (
          validPhoneNumber ? (
            <ListItemText
              primary={`${translate(
                "listingsMatch.contactPicker.add"
              )} ${formattedLocalPhoneNumber}`}
            />
          ) : (
            <ListItemText
              primary={`"${contactPhone}" ${translate(
                "listingsMatch.contactPicker.isNotAValidPhoneNumber"
              )}`}
            />
          )
        ) : (
          <ListItemText
            primary={callerFullName || formattedLocalPhoneNumber}
            secondary={callerFullName ? formattedLocalPhoneNumber : undefined}
          />
        )}
        {/* ONLY when a COLLEAGUE handles them. A green ring on every one of my own contacts,
            in my own picker, is fifty rings that mean nothing — the badge earns its place
            here precisely by being rare.

            `marginInlineStart: "auto"`, never `ml`: a physical margin is mirrored by
            stylis-plugin-rtl and would push the badge off the option's reading START. */}
        {(() => {
          const handler = handlerFor?.(contactPhone);
          return handler && !handler.isMe && !handler.stale ? (
            <Box sx={{ marginInlineStart: "auto", display: "flex", flexShrink: 0 }}>
              <CrmHandlerBadge handler={handler} size="xs" reserve={false} />
            </Box>
          ) : null;
        })()}
      </ListItem>
    );
  };
}

type NameSelection = (contact: ContactPickerOption) => ContactPickerOption;

function handleRenderTag(named: NameSelection, handlerFor?: HandlerLookup) {
  return (
    value: ContactPickerOption[],
    getTagProps: AutocompleteRenderGetTagProps
  ): JSX.Element[] => {
    return value.map((contact, index) => {
      const { callerFirstName, callerLastName, id: contactPhone } = named(contact);

      const callerFullName = getFullName(callerFirstName, callerLastName);

      const parsedPhoneNumber =
        parsePhoneNumber(contactPhone)?.formatNational() ?? contactPhone;

      const label = callerFullName || parsedPhoneNumber || contactPhone;

      if (!label) return null;

      /* THE TAG IS WHAT REMAINS ON SCREEN AFTER THE MENU CLOSES, so without the face here
         the warning the option row carried vanishes at exactly the moment the user commits
         to the contact. A `Chip` is legitimate for this (interactive, with `onDelete`) —
         the "prefer Label" rule names that as its own exception. */
      const handler = handlerFor?.(contactPhone);
      const colliding = handler && !handler.isMe && !handler.stale ? handler : null;

      return (
        <Chip
          title={colliding ? `${contactPhone} · מתווך מטפל: ${colliding.name}` : contactPhone}
          avatar={colliding ? <CrmMemberAvatar name={colliding.name} avatarUrl={colliding.avatarUrl} size={24} /> : undefined}
          label={label}
          {...getTagProps({ index })}
        />
      );
    });
  };
}

function handleRenderInput(
  translate: TFunction,
  loading: boolean,
  label: string | null = null
) {
  return (params: AutocompleteRenderInputParams): JSX.Element => {
    return (
      <TextField
        {...params}
        label={label ?? translate("listingsMatch.contactPicker.title")}
        InputProps={{
          ...params.InputProps,
          endAdornment: loading ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            params.InputProps.endAdornment
          )
        }}
      />
    );
  };
}

function handleGetOptionLabel(named: NameSelection) {
  return (contact: string | ContactPickerOption): string => {
    // `freeSolo`: while the realtor is typing, the "option" is the typed text itself.
    if (typeof contact === "string") return contact;

    return labelOf(named(contact));
  };
}

function handleIsOptionEqualToValue(option: ContactPickerOption, value: ContactPickerOption): boolean {
  return option.id === value.id;
}
